import { z } from 'zod';

/**
 * OneSignal config (settings key `onesignal`). The REST API key is a secret —
 * API reads never return it, only `restApiKeySet`. The App ID is public (the
 * app/web SDK uses it) so it is returned as-is.
 *
 * There is deliberately NO "auto-notify on these events" setting.
 *
 * There used to be, and it was a live incident: business events were pushed to
 * the configured SEGMENT, which on the shop meant "Total Subscriptions" — every
 * phone with the app on it. Every till sale, every posted invoice, every
 * payment and even HR payroll was broadcast to the whole customer base. The
 * control read "Auto-notify on: POS sale", so the shop reasonably understood it
 * as "tell me", and nothing on screen said the audience was everyone.
 *
 * Nothing was lost by removing it, because every real audience already has its
 * own TARGETED path, and had all along:
 *   • the customer who ordered  → PortalNotificationsService.notifyCustomer(partyId)
 *   • the owner / app manager   → ManagerNotificationsService → notifyExternalIds(logins)
 *   • staff in the office       → the header bell (permission-filtered) + Telegram
 * If a shop event should reach somebody, add it to one of those — never to a
 * segment, which cannot be anything but a broadcast.
 */
export interface OneSignalConfig {
  enabled: boolean;
  appId: string | null;
  restApiKeySet: boolean;
  /** Segment used by MANUAL broadcasts from the send page only (e.g. "Subscribed Users"). */
  defaultSegment: string;
  /**
   * Devices OneSignal currently considers reachable, read live from its API.
   * The single most useful number when "notifications aren't working": zero
   * means no phone has ever subscribed, and no amount of composing will help.
   * null when it could not be read (not configured, or OneSignal unreachable).
   */
  subscribedDevices?: number | null;
}

const APP_ID = z.string().uuid();

export const OneSignalConfigUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  appId: APP_ID.nullable().optional(),
  /** Set/replace the REST API key; null clears it; omit to leave unchanged. */
  restApiKey: z.string().min(10).max(200).nullable().optional(),
  defaultSegment: z.string().min(1).max(120).optional(),
});
export type OneSignalConfigUpdate = z.infer<typeof OneSignalConfigUpdateSchema>;

/** Manual broadcast from the "send notifications" page. */
export const PushSendSchema = z.object({
  title: z.string().min(1).max(200),
  message: z.string().min(1).max(1000),
  segment: z.string().min(1).max(120).optional(),
  /**
   * Send to ONE customer instead of a segment — their app accounts' devices, and
   * their in-app inbox alone. Takes precedence over `segment` when both arrive.
   * The customer is named by PARTY id (what the office knows them by); the
   * OneSignal external id is their portal-login id, resolved server-side.
   */
  partyId: z.string().uuid().optional(),
  /**
   * Send to one CUSTOMER GROUP — every customer assigned to this price list
   * (قرطاسية / ماركيت / …), which is what a shop means by "the stationery
   * customers". Ignored when `partyId` is set.
   */
  priceListId: z.string().uuid().optional(),
  url: z.string().url().max(500).optional(),
});
export type PushSendInput = z.infer<typeof PushSendSchema>;

export interface PushSendResult {
  ok: boolean;
  recipients?: number;
  /** App accounts the send was addressed to (0 ⇒ nobody in this audience has one). */
  targeted?: number;
  id?: string | null;
  error?: string | null;
  /** How many customer in-app inboxes the broadcast was written to (mobile
   *  Notifications page). Set for manual broadcasts; independent of device push. */
  inbox?: number;
}

/**
 * Whether one customer can actually be reached by device push, read live from
 * OneSignal. The answer to "I sent it and never got it" — which until now was
 * invisible from the office.
 */
export interface CustomerPushStatus {
  /** App logins this customer has (0 ⇒ they have never signed in on the app). */
  accounts: number;
  /** Devices OneSignal knows for those logins (0 ⇒ the app was never opened on a phone that links push). */
  devices: number;
  /** Of those, how many currently ACCEPT notifications (permission granted). */
  subscribed: number;
  /** False when OneSignal could not be reached — the numbers above are then unknown, not zero. */
  checked: boolean;
}

/** One row in the send history. */
export interface PushNotificationLog {
  id: string;
  title: string;
  body: string;
  audience: string;          // 'segment' | 'event'
  segment: string | null;
  eventType: string | null;
  /**
   * Devices OneSignal said it queued to. NULL when it did not say — which is
   * every alias-targeted send (one customer, a group, a manager), so this is
   * shown as "—" rather than a zero that reads like a failed delivery.
   */
  recipients: number | null;
  /** App accounts the send aimed at — resolved by us, so always known. */
  targeted: number | null;
  /** In-app inboxes actually written; what the customer sees in the app. */
  inbox: number | null;
  status: string;            // 'sent' | 'failed'
  error: string | null;
  createdAt: string;
}

/** What a send would reach, asked BEFORE sending. */
export interface PushAudiencePreview {
  /** Customers in the chosen audience (1 for a single customer). */
  customers: number;
  /** Of those, how many have at least one app account signed in. */
  appAccounts: number;
  /** Devices known to be subscribed — only known for the whole-shop audience. */
  devices: number | null;
  /** Human label for the audience, e.g. the group or customer name. */
  label: string | null;
}
